import { isLinkGuestEmail } from '@werft/domain';
import { TASK_DETAIL_TABS } from '../features/project/detailTabs.js';
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { accentStyle } from '../platform/branding.js';
import { resolveKey } from '../platform/taskKeys.js';
import { z } from 'zod';
import { LoginPage } from '../features/auth/LoginPage.js';
import { ProjectPage } from '../features/project/ProjectPage.js';
import { t } from '../i18n/index.js';
import { useConfig, useMe } from '../platform/queries.js';
import { draftUser, setDraftUser } from '../platform/drafts.js';
import { realtime } from '../platform/realtime.js';
import { outbox } from '../platform/sync.js';
import { Notice } from '../ui/ui.js';
import { Shell } from './Shell.js';

// Audit F13: Seiten, die beim Start nicht gebraucht werden (Verwaltung, Einrichtung, Einladung, Einstellungen, öffentliche
// Freigabe), getrennt laden – das Startpaket bleibt klein.
const WorkspacesPage = lazy(() => import('../features/workspace/WorkspacesPage.js').then(m => ({ default: m.WorkspacesPage })));
const ExternalAccessPage = lazy(() => import('../features/admin/ExternalAccess.js'));
const AdminPage = lazy(() => import('../features/admin/AdminPage.js').then((m) => ({ default: m.AdminPage })));
const InvitationPage = lazy(() => import('../features/auth/InvitationPage.js').then((m) => ({ default: m.InvitationPage })));
const SetupPage = lazy(() => import('../features/auth/SetupPage.js').then((m) => ({ default: m.SetupPage })));
const WorkspaceSettingsPage = lazy(() => import('../features/workspace/WorkspaceSettingsPage.js').then((m) => ({ default: m.WorkspaceSettingsPage })));
// ADR 0124: Lobby getrennt geladen – das Startpaket bis zur ersten Aufgabe trägt sie nicht mit
const LobbyPage = lazy(() => import('../features/lobby/LobbyPage.js').then((m) => ({ default: m.LobbyPage })));
const MyDayPage = lazy(() => import('../features/myday/MyDayPage.js').then((m) => ({ default: m.MyDayPage })));
const SharedPage = lazy(() => import('../features/share/SharedPage.js').then((m) => ({ default: m.SharedPage })));
const ProjectLinkJoin = lazy(() => import('../features/share/ProjectLinkJoin.js').then((m) => ({ default: m.ProjectLinkJoin })));

/**
 * Teilbare, stabile URLs (Leitlinie, Seite 7). Projekte bekommen in Stufe 1 eigene Routen unter /p/$projectId,
 * in denen kein anderes Projekt sichtbar ist (Insel-Prinzip).
 */
const rootRoute = createRootRoute({
  // getrennt geladene Seiten (siehe oben) – kurzer Moment ohne Inhalt statt eines Ladehinweises
  component: function Root() {
    const config = useConfig();
    useEffect(() => {
      const style = accentStyle(config.data?.color ?? '#c2410c');
      for (const [name, value] of Object.entries(style)) document.documentElement.style.setProperty(name, String(value));
    }, [config.data?.color]);
    return <Suspense fallback={null}><Outlet /></Suspense>;
  },
});

/** Angemeldeter Bereich: prüft Einrichtung und Sitzung, startet den Live-Abgleich */
function Authed({ children, bare }: { children: React.ReactNode; bare?: boolean }) {
  const config = useConfig();
  const me = useMe();
  const userId = me.data?.user.id ?? null;
  useEffect(() => {
    if (me.data) realtime.start();
  }, [me.data]);
  // Offene Änderungen und Entwürfe gehören genau dieser Person
  // Entwürfe sofort (vor dem ersten Zeichnen der Kinder) – mit gespeichertem Stand zeichnen sie sofort (P10)
  if (draftUser() !== userId) setDraftUser(userId);
  useEffect(() => {
    outbox.setUser(userId);
  }, [userId]);
  // Abnahme 0.63: Linkgast-Zugang wieder aktiv (Teilen per Link wieder an, Sperre aufgehoben) – frisch laden, weil der
  // Live-Abgleich die Projektdaten beim Ruhen entfernt hat
  const linkState = me.data?.linkAccess?.state ?? null;
  const prevLink = useRef(linkState);
  useEffect(() => {
    if (prevLink.current && prevLink.current !== 'active' && linkState === 'active') {
      const pid = me.data?.linkAccess?.projectId;
      // beim Ruhen hat die Projektseite in die Lobby gewechselt – zurück ins Projekt
      if (pid && !location.pathname.startsWith(`/p/${pid}`)) location.assign(`/p/${pid}`);
      else location.reload();
    }
    prevLink.current = linkState;
  }, [linkState]); // eslint-disable-line react-hooks/exhaustive-deps
  if (config.isPending || me.isPending) return null;
  // Ohne Netz bleibt der zuletzt geladene Stand gültig – ein Fehler zählt nur, wenn es gar keine Daten gibt
  if ((config.isError && !config.data) || (me.isError && me.data === undefined)) return <Notice kind="error">{t('error.generic')}</Notice>;
  if (config.data.needsSetup) return <Navigate to="/einrichtung" />;
  // N01: Zugang über einen Projektlink beendet (Widerruf, Ablauf) – verständlich sagen statt zur Anmeldung zu schicken
  if (!me.data && wasLinkGuest()) return <LinkAccessEnded />;
  if (!me.data) return <Navigate to="/anmelden" search={{ redirect: location.pathname + location.search }} />;
  rememberLinkGuest(isLinkGuestEmail(me.data.user.email));
  // Abnahme 0.63: Linkgast-Zugang ruht (Teilen per Link aus, Notfallsperre, abgelaufen) – verständlich sagen, live
  const la = me.data.linkAccess;
  if (la && la.state !== 'active') return <LinkAccessPaused state={la.state} project={la.project} onRetry={() => void me.refetch()} />;
  // Projekte bringen ihren eigenen, ruhigen Rahmen mit (Insel-Prinzip)
  return bare ? <>{children}</> : <Shell>{children}</Shell>;
}

const LINK_KEY = 'werft.linkguest';
function wasLinkGuest() {
  try {
    return sessionStorage.getItem(LINK_KEY) === '1' || localStorage.getItem(LINK_KEY) === '1';
  } catch {
    return false;
  }
}
function rememberLinkGuest(on: boolean) {
  try {
    if (on) localStorage.setItem(LINK_KEY, '1');
    else localStorage.removeItem(LINK_KEY);
  } catch {
    /* egal */
  }
}
function LinkAccessEnded() {
  return (
    <div className="mx-auto max-w-md p-6">
      <Notice kind="error">
        <span data-access-ended="">{t('access.ended')}</span>
      </Notice>
      <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">{t('join.invalid')}</p>
      <button
        type="button"
        className="mt-4 min-h-11 text-sm font-medium text-[var(--color-accent)]"
        onClick={() => {
          rememberLinkGuest(false);
          location.assign('/anmelden');
        }}
      >
        {t('login.title')}
      </button>
    </div>
  );
}

function LinkAccessPaused({ state, project, onRetry }: { state: 'paused' | 'locked' | 'expired'; project: string; onRetry: () => void }) {
  return (
    <div className="mx-auto max-w-md p-6" data-link-paused={state}>
      <Notice kind="error">
        <span className="font-medium">{t(`linkAccess.${state}`, { project })}</span>
      </Notice>
      <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">{t(`linkAccess.${state}Hint`)}</p>
      {state !== 'expired' && (
        <button type="button" className="mt-4 min-h-11 text-sm font-medium text-[var(--color-accent)]" onClick={onRetry}>
          {t('linkAccess.retry')}
        </button>
      )}
    </div>
  );
}

const lobbyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: z.object({ w: z.string().optional(), notice: z.string().optional() }),
  component: function Lobby() {
    const { w, notice } = lobbyRoute.useSearch();
    return (
      <Authed>
        <LobbyPage selected={w} notice={notice} />
      </Authed>
    );
  },
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$workspaceId/einstellungen',
  component: function WorkspaceSettings() {
    const { workspaceId } = workspaceSettingsRoute.useParams();
    return (
      <Authed>
        <WorkspaceSettingsPage workspaceId={workspaceId} />
      </Authed>
    );
  },
});

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId',
  validateSearch: z.object({ aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined), ansicht: z.enum(['liste', 'board', 'tabelle', 'kalender', 'zeitachse']).optional().catch(undefined) }),
  component: function Project() {
    const { projectId } = projectRoute.useParams();
    const { aufgabe, aufgabenbereich, ansicht } = projectRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} view={ansicht} />
      </Authed>
    );
  },
});

/** Projektübersicht (Paket P9) – ein Werkzeug des Projekts, zugeschaltet von der Projektleitung */
const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/uebersicht',
  validateSearch: z.object({ aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined) }),
  component: function Overview() {
    const { projectId } = overviewRoute.useParams();
    const { aufgabe, aufgabenbereich } = overviewRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="overview" />
      </Authed>
    );
  },
});

/** Projektablage (Paket P11): Ordner, Papierkorb und „An Aufgaben“ über Suchparameter, Vorschau über `datei` */
const filesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/dateien',
  validateSearch: z.object({
    ordner: z.string().optional(),
    ablage: z.enum(['ablage', 'papierkorb', 'aufgaben']).optional().catch(undefined),
    datei: z.string().optional(),
    // Abnahme 0.58 F02: eine bestimmte Rückmeldung im Datei-Arbeitsplatz zeigen
    rueckmeldung: z.string().optional(),
    // 0.149 (ADR 0151): Suche und Filter der Ablage – nur Eingrenzung, nie Rechte
    suche: z.string().max(200).optional().catch(undefined),
    umfang: z.literal('projekt').optional().catch(undefined),
    typ: z.string().max(200).optional().catch(undefined),
    datum: z.enum(['updated', 'created', 'version']).optional().catch(undefined),
    von: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
    bis: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
    person: z.string().max(100).optional().catch(undefined),
    offen: z.literal('1').optional().catch(undefined),
    abnahme: z.enum(['pending', 'approved', 'changes', 'none']).optional().catch(undefined),
    neu: z.literal('1').optional().catch(undefined),
    tag: z.string().max(2000).optional().catch(undefined),
    tagmodus: z.literal('alle').optional().catch(undefined),
    layout: z.literal('raster').optional().catch(undefined),
    aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined),
  }),
  component: function Files() {
    const { projectId } = filesRoute.useParams();
    const { ordner, ablage, datei, aufgabe, aufgabenbereich } = filesRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="files" files={{ folderId: ordner ?? null, mode: ablage ?? 'ablage', fileId: datei }} />
      </Authed>
    );
  },
});

/** Notizen (Paket P12): offene Seite über `seite` */
const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/notizen',
  validateSearch: z.object({ seite: z.string().optional(), aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined), neu: z.literal('1').optional().catch(undefined) }),
  component: function Docs() {
    const { projectId } = docsRoute.useParams();
    const { seite, aufgabe, aufgabenbereich, neu } = docsRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="docs" pageId={seite} newPage={!!neu} />
      </Authed>
    );
  },
});

/** Gespräche (Paket P13): Themen (`thema`) oder Chat (`ansicht=chat`, `nachricht` springt hin) */
const talkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/gespraeche',
  validateSearch: z.object({
    thema: z.string().optional(),
    ansicht: z.enum(['chat']).optional().catch(undefined),
    nachricht: z.string().optional(),
    aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined),
    /** Audit F07: „Erstellen → Thema“ von der Startseite */
    neu: z.literal('1').optional().catch(undefined),
  }),
  component: function Talk() {
    const { projectId } = talkRoute.useParams();
    const { thema, ansicht, nachricht, aufgabe, aufgabenbereich, neu } = talkRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="talk" talk={{ mode: ansicht === 'chat' ? 'chat' : 'themen', topicId: thema, messageId: nachricht, newTopic: !!neu }} />
      </Authed>
    );
  },
});

/** Projekteingang (ADR 0064): Vorgang (`vorgang`), Zustandsfilter (`zustand`) */
const intakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/eingang',
  validateSearch: z.object({
    vorgang: z.string().optional(),
    // Werte wie INTAKE_STATES (contracts) – hier ausgeschrieben, damit das Startpaket die Eingangs-Schemas nicht lädt
    zustand: z.enum(['new', 'clarify', 'assigned', 'closed', 'spam']).optional().catch(undefined),
    formulare: z.literal('1').optional().catch(undefined),
    aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined),
  }),
  component: function Intake() {
    const { projectId } = intakeRoute.useParams();
    const { vorgang, zustand, formulare, aufgabe, aufgabenbereich } = intakeRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="intake" intake={{ itemId: vorgang, state: zustand, forms: !!formulare }} />
      </Authed>
    );
  },
});

/** Termine (ADR 0072): offener Termin (`termin`) */
const eventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/termine',
  validateSearch: z.object({ termin: z.string().optional(), aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined) }),
  component: function Events() {
    const { projectId } = eventsRoute.useParams();
    const { termin, aufgabe, aufgabenbereich } = eventsRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="events" events={{ eventId: termin }} />
      </Authed>
    );
  },
});

/** Personen & Teilnahme (ADR 0080): offener Kontakt (`kontakt`) */
const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/personen',
  validateSearch: z.object({ kontakt: z.string().optional(), neu: z.literal('1').optional().catch(undefined), aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined) }),
  component: function People() {
    const { projectId } = peopleRoute.useParams();
    const { kontakt, neu, aufgabe, aufgabenbereich } = peopleRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="people" persons={{ contactId: kontakt, create: !!neu }} />
      </Authed>
    );
  },
});

/** Zeit & Nachweise (ADR 0085) */
const timeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/zeit',
  validateSearch: z.object({ aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined), bereich: z.enum(['erfassen', 'auswerten', 'nachweise']).optional().catch(undefined), kosten: z.string().uuid().optional().catch(undefined) }),
  component: function Time() {
    const { projectId } = timeRoute.useParams();
    const { aufgabe, aufgabenbereich, bereich } = timeRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="time" timeSection={bereich} />
      </Authed>
    );
  },
});

/** Ziele & Risiken (ADR 0084): geöffnetes Ziel (`ziel`) oder Risiko (`risiko`) */
const goalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/ziele',
  validateSearch: z.object({ ziel: z.string().optional(), risiko: z.string().optional(), aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined) }),
  component: function Goals() {
    const { projectId } = goalsRoute.useParams();
    const { ziel, risiko, aufgabe, aufgabenbereich } = goalsRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="goals" goals={{ goalId: ziel, riskId: risiko }} />
      </Authed>
    );
  },
});

/** Ressourcen & Einsätze (ADR 0083): geöffneter Einsatz (`einsatz`) */
const resourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/einsaetze',
  validateSearch: z.object({ einsatz: z.string().optional(), tag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined), aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined) }),
  component: function Resources() {
    const { projectId } = resourcesRoute.useParams();
    const { einsatz, tag, aufgabe, aufgabenbereich } = resourcesRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="resources" shifts={{ shiftId: einsatz, day: tag }} />
      </Authed>
    );
  },
});

/** Budget & Kosten (ADR 0082) */
const budgetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/budget',
  validateSearch: z.object({ position: z.string().optional(), aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined) }),
  component: function Budget() {
    const { projectId } = budgetRoute.useParams();
    const { position, aufgabe, aufgabenbereich } = budgetRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="budget" budget={{ lineId: position }} />
      </Authed>
    );
  },
});

/** Freigaben-Register (ADR 0071): Filter (`filter`); ADR 0077: Entscheidung (`entscheidung`), neue Anfrage (`neu`, `thema`) */
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$projectId/freigaben',
  validateSearch: z.object({
    filter: z.enum(['mine', 'open', 'decided']).optional().catch(undefined),
    entscheidung: z.string().optional(),
    neu: z.literal('1').optional().catch(undefined),
    thema: z.string().optional(),
    aufgabe: z.string().optional(), aufgabenbereich: z.enum(TASK_DETAIL_TABS).optional().catch(undefined),
  }),
  component: function Register() {
    const { projectId } = registerRoute.useParams();
    const { filter, entscheidung, neu, thema, aufgabe, aufgabenbereich } = registerRoute.useSearch();
    return (
      <Authed bare>
        <ProjectPage key={projectId} projectId={projectId} taskId={aufgabe} taskTab={aufgabenbereich} tool="approvals" register={{ filter, decisionId: entscheidung, create: neu === '1', topicId: thema }} />
      </Authed>
    );
  },
});

/** Öffentliches Formular (ADR 0064) – ohne Anmeldung, getrennt geladen */
const PublicForm = lazy(() => import('../features/intake/PublicForm.js'));
const formRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/f/$token',
  component: function Form() {
    const { token } = formRoute.useParams();
    return (
      <Suspense fallback={null}>
        <PublicForm token={token} />
      </Suspense>
    );
  },
});

/** ADR 0099: Statusseite und Bestätigung einer Einreichung – ohne Anmeldung, getrennt geladen */
const PublicStatus = lazy(() => import('../features/intake/PublicStatus.js'));
const ConfirmSubmission = lazy(() => import('../features/intake/PublicStatus.js').then((m) => ({ default: m.ConfirmSubmission })));
const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stand/$token',
  component: function Status() {
    const { token } = statusRoute.useParams();
    return (
      <Suspense fallback={null}>
        <PublicStatus token={token} />
      </Suspense>
    );
  },
});
const confirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/f/bestaetigen/$token',
  component: function Confirm() {
    const { token } = confirmRoute.useParams();
    return (
      <Suspense fallback={null}>
        <ConfirmSubmission token={token} />
      </Suspense>
    );
  },
});

const myDayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/heute',
  component: () => (
    <Authed>
      <MyDayPage />
    </Authed>
  ),
});

const workspacesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/arbeitsgruppen', component: () => <Authed><WorkspacesPage /></Authed> });

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verwaltung',
  component: () => (
    <Authed>
      <AdminPage />
    </Authed>
  ),
});

/** W10: Externe Zugriffe – für alle Angemeldeten; der Server zeigt nur den zulässigen Ausschnitt */
const externalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/externe-zugriffe',
  validateSearch: z.object({ projekt: z.string().optional() }),
  component: function External() {
    const { projekt } = externalRoute.useSearch();
    return (
      <Authed>
        <ExternalAccessPage projectId={projekt} />
      </Authed>
    );
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/anmelden',
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: function Login() {
    const { redirect } = loginRoute.useSearch();
    const me = useMe();
    if (me.data) return <Navigate to={redirect && redirect.startsWith('/') ? redirect : '/'} />;
    return <LoginPage redirect={redirect} />;
  },
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einrichtung',
  component: function Setup() {
    const config = useConfig();
    if (config.data && !config.data.needsSetup) return <Navigate to="/" />;
    return <SetupPage />;
  },
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/einladung/$token',
  component: function Invitation() {
    const { token } = invitationRoute.useParams();
    return <InvitationPage token={token} />;
  },
});

/** P25: Aufgaben-Schlüssel als Link („/a/KURS-42“) – löst auf und springt in die Aufgabe */
const keyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/a/$key',
  component: function KeyLink() {
    const { key } = keyRoute.useParams();
    return (
      <Authed>
        <KeyRedirect taskKey={key} />
      </Authed>
    );
  },
});

function KeyRedirect({ taskKey }: { taskKey: string }) {
  const navigate = useNavigate();
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let on = true;
    resolveKey(taskKey)
      .then((r) => on && void navigate({ to: '/p/$projectId', params: { projectId: r.projectId }, search: { aufgabe: r.taskId }, replace: true }))
      .catch(() => on && setMissing(true));
    return () => {
      on = false;
    };
  }, [taskKey, navigate]);
  return missing ? <Notice kind="error">{t('key.notFound', { key: taskKey.toUpperCase() })}</Notice> : <p className="text-sm text-zinc-500">{t('common.loading')}</p>;
}

/** P20: Freigabelink – öffentlich, ohne Anmeldung und ohne Rahmen */
const sharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$token',
  component: function Shared() {
    const { token } = sharedRoute.useParams();
    return <SharedPage token={token} />;
  },
});

/** N01: Projektlink – Name angeben und das ganze Projekt öffnen (ohne Rahmen) */
const projectLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$token',
  component: function ProjectLink() {
    const { token } = projectLinkRoute.useParams();
    return <ProjectLinkJoin token={token} />;
  },
});

const routeTree = rootRoute.addChildren([sharedRoute, projectLinkRoute, keyRoute, lobbyRoute, myDayRoute, projectRoute, overviewRoute, filesRoute, docsRoute, talkRoute, intakeRoute, registerRoute, eventsRoute, peopleRoute, budgetRoute, resourcesRoute, goalsRoute, timeRoute, formRoute, statusRoute, confirmRoute, workspaceSettingsRoute, workspacesRoute, adminRoute, externalRoute, loginRoute, setupRoute, invitationRoute]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
